import { supabase, GALLERY_BUCKET, GALLERY_TABLE } from "./supabase";

export type GalleryImage = {
  id: string;
  storagePath: string;
  url: string;
  caption: string | null;
  sortIndex: number;
  createdAt: string;
};

type GalleryRow = {
  id: string;
  storage_path: string;
  caption: string | null;
  sort_index: number;
  created_at: string;
};

const toImage = (row: GalleryRow): GalleryImage => {
  const { data } = supabase.storage.from(GALLERY_BUCKET).getPublicUrl(row.storage_path);
  return {
    id: row.id,
    storagePath: row.storage_path,
    url: data.publicUrl,
    caption: row.caption,
    sortIndex: row.sort_index,
    createdAt: row.created_at,
  };
};

export const listGalleryImages = async (): Promise<GalleryImage[]> => {
  const { data, error } = await supabase
    .from(GALLERY_TABLE)
    .select("id, storage_path, caption, sort_index, created_at")
    .order("sort_index", { ascending: true })
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data ?? []).map(toImage);
};

export const uploadGalleryImage = async (
  file: File,
  opts: { caption?: string | null; sortIndex?: number } = {},
): Promise<GalleryImage> => {
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "jpg";
  const id = crypto.randomUUID();
  const storagePath = `images/${id}.${ext}`;

  const { error: uploadErr } = await supabase.storage
    .from(GALLERY_BUCKET)
    .upload(storagePath, file, {
      cacheControl: "31536000",
      upsert: false,
      contentType: file.type || undefined,
    });
  if (uploadErr) throw uploadErr;

  const { data, error: insertErr } = await supabase
    .from(GALLERY_TABLE)
    .insert({
      id,
      storage_path: storagePath,
      caption: opts.caption ?? null,
      sort_index: opts.sortIndex ?? Math.floor(Date.now() / 1000),
    })
    .select("id, storage_path, caption, sort_index, created_at")
    .single();
  if (insertErr) throw insertErr;
  return toImage(data);
};

export const deleteGalleryImage = async (image: GalleryImage): Promise<void> => {
  const { error: storageErr } = await supabase.storage
    .from(GALLERY_BUCKET)
    .remove([image.storagePath]);
  if (storageErr) throw storageErr;
  const { error: rowErr } = await supabase
    .from(GALLERY_TABLE)
    .delete()
    .eq("id", image.id);
  if (rowErr) throw rowErr;
};

export const reorderGalleryImages = async (ordered: GalleryImage[]): Promise<void> => {
  const updates = ordered.map((img, idx) => ({
    id: img.id,
    storage_path: img.storagePath,
    sort_index: idx,
  }));
  const { error } = await supabase.from(GALLERY_TABLE).upsert(updates, { onConflict: "id" });
  if (error) throw error;
};
